import { normalizeDecimalInput, numberInputModelString, dotDecimalDisplay, isNumberInputText, ACCEPT_DOT_DECIMAL_CSS_CLASS } from '../../../shared/helpers/decimal-input/decimal-input';
import { Component, OnInit, Input, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { SemTextInputComponent } from '../sem-text-input/sem-text-input.component';
import { MinLengthValidator, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessorBase } from '../shared/value-accessor/value-accessor-base';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'sem-text-box',
    templateUrl: './sem-text-box.component.html',
    styleUrls: ['./sem-text-box.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemTextBoxComponent, multi: true }
    ],
    standalone: false
})

export class SemTextBoxComponent extends ValueAccessorBase<string> implements OnInit {

  @Input() fields: any;
  @Input() isNumberInput: boolean = false;
  // Opt-in "dot as decimal" for NumberInput controls (BRD coordinates). Set by the form config
  // through the control's CssClass setting (wg-accept-dot-decimal on the wrapper div) or this input.
  @Input() acceptDotDecimal: boolean = false;
  @Input() isPasswordInput: boolean = false;
  @Input() showTooltipValue: any;
  disResults: any[] = [];
  @Input() isActiveUrl: boolean = false;
  @Input() step: string = "1";
  @Input() maxLength: string = "";
  @Input() isIntegerInput: boolean = false;
  @Input() isAlphaInput: boolean = false;
  @Input() isCurrencyInput: boolean = false;
  @Input() currencySign: string = '';
  @Input() hiddenValueCondition: string;
  @Input() retryBindingProp: string;
  @Input() showCopyIconOnReadOnly: boolean = false;
  isHiddenValue: boolean = false;
  copied: boolean = false;

  copyValueToClipboard() {
    if (this.value == null || this.value.toString().length === 0) {
      return;
    }
    this.writeToClipboard(this.value.toString());
  }

  copyDisplayedValue() {
    const displayed = this.numInput && this.numInput.nativeElement ? this.numInput.nativeElement.value : null;
    const text = (displayed != null && displayed.length > 0) ? displayed : (this.value != null ? this.value.toString() : '');
    if (text.length === 0) {
      return;
    }
    this.writeToClipboard(text);
  }

  private writeToClipboard(text: string) {
    const onSuccess = () => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1500);
    };
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(text).then(onSuccess).catch(() => this.fallbackCopy(text, onSuccess));
    } else {
      this.fallbackCopy(text, onSuccess);
    }
  }

  private fallbackCopy(text: string, onSuccess: () => void) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      onSuccess();
    } catch {
    }
  }

  // advisory Slovene spell-check (opt-in per field via the designer)
  @Input() spellCheck: boolean | string = false;
  @Input() spellCheckLanguage: string = 'sl';
  @Input() spellCheckClassType: string = '';
  @Input() spellCheckField: string = '';

  @ViewChild("numInput") numInput: ElementRef;
  @ViewChild("alphaInput") alphaInput: ElementRef;
  @ViewChild("intInput") intInput: ElementRef;
  maxLengthInteger: number = 1000; //default value

  // Fake šifrant (soft codelist / history autocomplete): suggest previously-entered values for THIS field,
  // read live from the search index. Stays free-text (Phase 1 = suggestions only, no lock/validation).
  // historyClassType + historyField are emitted by the backend form-template generator as literal bindings.
  @Input() historyAutocomplete: boolean = false;
  @Input() lockable: boolean = false; // controlled vocabulary: block values outside the existing set (see onHistoryBlur)
  @Input() containsSearch: boolean = true; // true = match term anywhere in the value; false = starts-with
  @Input() historyClassType: string = "";
  @Input() historyField: string = "";
  historySuggestions: string[] = [];
  lockError: boolean = false; // set when a locked field is given a new value the user may not add
  private historyReq: Subject<void> = new Subject<void>();
  private lockCheckReq: Subject<void> = new Subject<void>();

  addResult() {
    this.disResults.push(this.checkForDisabled(this.getAttribute('name')));
  }

  super() { }

  ngOnInit() {

    if (this.isIntegerInput) {
      this.isPasswordInput = false;
      this.isNumberInput = false;
    }

    super.ngOnInit();

    if (!this.acceptDotDecimal && this.isNumberInput) {
      const host: any = this.control && this.control.nativeElement;
      this.acceptDotDecimal = !!(host && host.closest && host.closest('.' + ACCEPT_DOT_DECIMAL_CSS_CLASS));
    }

    this.isHiddenValue = false;
    if (this.hiddenValueCondition != null && typeof this.hiddenValueCondition != "undefined" && this.hiddenValueCondition.length > 0) {
      this.isHiddenValue = eval(this.hiddenValueCondition);
    }

    /*if (this.isNumberInput == null || typeof this.isNumberInput == "undefined") {
      this.isNumberInput = false;
    } else {
      this.restProviderService.modelValueChanged.subscribe(model => {

        if (this.model != null && typeof this.model != "undefined" && Object.keys(model).length > 3) {
          let bp = this.getAttribute('name').replace("model.", "");

          let v = this.dfms.getValue(this.model, bp);
          
          if (v) {
            this.value = v.toLocaleString("sl-SI");
          }

        }

      });
    }*/

    if (this.showTooltipValue == null || typeof this.showTooltipValue == "undefined") {
      this.showTooltipValue = false;
    }

    try {
      if (this.maxLength && this.maxLength.length > 0) {
        this.maxLengthInteger = parseInt(this.maxLength.trim());
      }
    } catch {

    }

  }

  private get dotDecimalMode(): boolean {
    return this.isNumberInput && this.acceptDotDecimal;
  }

  // What the number input shows. Opt-in fields show the decimal as a dot; `value` (the model,
  // the form control and what is saved) always keeps the Slovene form the server parses.
  get numberDisplayValue(): string {
    return this.dotDecimalMode ? dotDecimalDisplay(this.value) : this.value;
  }

  // Number input typed: opt-in fields turn the typed text into the Slovene form BEFORE anything
  // leaves the control (onTextChange writes it into the form control, the setter into the model).
  onNumberModelChange(typed: string) {
    const v = this.dotDecimalMode ? normalizeDecimalInput(typed) : typed;
    this.onTextChange(v);
    this.value = v;
  }

  protected override valueChanged(): void {

    // Skip locale formatting when maxLength is set — toLocaleString adds thousand separators
    // which inflate the character count and cause maxlength truncation at the wrong position.
    if (!isNaN(Number(this.value)) && this.isNumberInput && !this.maxLength) {
      let v: number = Number(this.value);
      // No rounding (toLocaleString's default of 3 decimals was written back into the model).
      this.value = numberInputModelString(v, this.dotDecimalMode);
    }

  }

  redirectToActiveUrl(value) {
    if (value != null && typeof value != "undefined" && value.length > 0) {
      window.open(value, "_blank");
    }
  }

  onRevealHiddenValue() {    
    this.isHiddenValue = false;        
    this.dfms.assign(this.model, this.retryBindingProp, true, true);    
  }

  /**
   * Fake šifrant (history autocomplete): fetch distinct previously-entered values for this field
   * (case-insensitive prefix) and show them as free-text suggestions. Never blocks typing.
   */
  loadHistorySuggestions(term: string) {
    if (!this.historyAutocomplete) {
      return;
    }

    let classType = this.historyClassType;
    let field = this.historyField;
    if (classType == null || classType == "" || field == null || field == "") {
      // Missing generator-supplied context; nothing to query against.
      return;
    }

    this.historyReq.next(); // cancel any in-flight request

    this.restProviderService.getFieldSuggestions(classType, field, term || "", 20, this.containsSearch)
      .pipe(takeUntil(this.historyReq))
      .subscribe(result => {
        this.zone.run(() => {
          this.historySuggestions = result || [];
          this.changeDetection.detectChanges();
        });
      },
        error => {
          // Suggestions are best-effort; failure must not disrupt free-text entry.
          this.historySuggestions = [];
        }
      );
  }

  /** Focus handler for history autocomplete: show the first values with an empty prefix. */
  onHistoryFocus() {
    if (this.historyAutocomplete) {
      this.loadHistorySuggestions(this.value || "");
    }
  }

  /** Option-selected handler: fill the value and run the normal text-change pipeline. */
  onHistorySelected(selected: string) {
    this.lockCheckReq.next(); // cancel any in-flight blur lock-check so it can't clobber this valid pick
    this.lockError = false; // picking an existing suggestion is always valid
    this.value = selected;
    this.onTextChange(selected);
  }

  /**
   * Does the current user have the "add new registry entry" permission?
   * Mirrors the Galis registry-add authorization: Administrators, or members of the RegistryEditor
   * usergroup (id 10), may introduce new values into a locked (controlled-vocabulary) field. Everyone
   * else must pick an existing value. Server re-checks this on save (authoritative).
   */
  private userCanAddNewValues(): boolean {
    const u: any = this.restProviderService?.currentUser;
    if (u == null) {
      return false;
    }
    if (u.IsAdmin === true) {
      return true;
    }
    const groups: any[] = u.Groups || [];
    // RegistryEditor usergroup id = 10 (see MEMORY: dbo.usergroup).
    return groups.some(g => g && g.Id === 10);
  }

  /**
   * Fake šifrant LOCK (controlled vocabulary): on blur, if the field is locked and the typed value is not
   * already in the existing set AND the user may not add new registry entries, block it (clear + show error).
   * Users WITH the right keep the new value. FE is best-effort; the server re-validates on save.
   */
  onHistoryBlur() {
    if (!this.historyAutocomplete || !this.lockable) {
      return;
    }

    const typed = (this.value || "").trim();
    if (typed.length === 0) {
      this.lockError = false; // empty is allowed
      return;
    }

    // Privileged users may add new values - never block them.
    if (this.userCanAddNewValues()) {
      this.lockError = false;
      return;
    }

    const classType = this.historyClassType;
    const field = this.historyField;
    if (!classType || !field) {
      return; // no context to validate against; do not block on missing config
    }

    this.lockCheckReq.next(); // cancel any in-flight lock check
    // Prefix query (starts-with) with a high cap: the exact value, if it exists, is guaranteed in the result.
    this.restProviderService.getFieldSuggestions(classType, field, typed, 1000, false)
      .pipe(takeUntil(this.lockCheckReq))
      .subscribe(result => {
        this.zone.run(() => {
          // A suggestion pick (or later edit) may have replaced the value while this check was in
          // flight - never clobber a value that is no longer the one we validated (race on click-select).
          if ((this.value || "").trim().toLowerCase() !== typed.toLowerCase()) {
            return;
          }
          const set = result || [];
          const exists = set.some(v => (v || "").toLowerCase() === typed.toLowerCase());
          if (!exists) {
            // Not in the controlled vocabulary and user may not add new values -> block.
            this.lockError = true;
            this.value = "";
            this.onTextChange("");
          } else {
            this.lockError = false;
          }
          this.changeDetection.detectChanges();
        });
      },
        error => {
          // Validation is best-effort on the FE (server is authoritative); do not block on transport failure.
          this.lockError = false;
        }
      );
  }

  onTextChange(value) {

    if (this.historyAutocomplete) {
      this.loadHistorySuggestions(value);
    }

    if (this.isNumberInput) {
      var isValid = isNumberInputText(value);
      if (!isValid) {
        value = "";
        this.value = "";
        this.dfms.assign(this.model, this.bindingProperty, "", true);
        if (this.numInput && this.numInput.nativeElement) {
          this.numInput.nativeElement.value = ""
        }
      }
    }

    if (this.isIntegerInput) {
      var isValid = /^\+?[0-9]*$/.test(value);
      if (!isValid) {
        value = value.replace(/[^0-9+]/g, '').replace(/(?!^)\+/g, '');
        this.value = value;
        this.dfms.assign(this.model, this.bindingProperty, value, true);
        if (this.intInput && this.intInput.nativeElement) {
          this.intInput.nativeElement.value = value;
        }
      }
    }

    if (this.isAlphaInput) {
      // Covers A-Z, Latin-1 Supplement and Latin Extended-A/B (excludes × U+00D7 and ÷ U+00F7)
      var isValid = /^[a-zA-ZÀ-ÖØ-öø-ɏ ]*$/.test(value);
      if (!isValid) {
        value = value.replace(/[^a-zA-ZÀ-ÖØ-öø-ɏ ]/g, '');
        this.value = value;
        this.dfms.assign(this.model, this.bindingProperty, value, true);
        if (this.alphaInput && this.alphaInput.nativeElement) {
          this.alphaInput.nativeElement.value = value;
        }
      }
    }

    let numberValue = parseFloat(value);

    if (!isNaN(this.min)) {
      let minNumber = parseFloat(this.min);
      if (numberValue < minNumber) {
        this.value = null;
        this.changeDetection.detectChanges();
        this.value = minNumber.toString();
        return;
      }
    }

    if (!isNaN(this.max)) {
      let maxNumber = parseFloat(this.max);
      if (numberValue > maxNumber) {
        this.value = null;
        this.changeDetection.detectChanges();
        this.value = maxNumber.toString();
        return;
      }
    }


    this.checkAfterInput(value);
  }

  checkAfterInput(value) {
    let attName = this.getAttribute("name");

    let control = this.dfms.globalForm.controls[attName];

    if (control !== null && typeof control !== "undefined") {
      this.dfms.globalForm.controls[attName].setValue(value);
      this.dfms.globalForm.controls[attName].updateValueAndValidity();
    }


    this.dfms.updateControlsValidationArray(attName, value);

    if (typeof this.onValueChangeEval != "undefined" && this.onValueChangeEval) {
      // Check if the string starts and ends with double quotes and remove them
      if (this.onValueChangeEval.startsWith('"') && this.onValueChangeEval.endsWith('"')) {
        this.onValueChangeEval = this.onValueChangeEval.substring(1, this.onValueChangeEval.length - 1);
      }
      eval(this.onValueChangeEval);
    }
  }

}
